import { getConfig } from '../api';
import { useAsync } from '../lib/async';
import { Empty, Id, Link, NotBuilt, Pill, Result, Toolbar } from '../lib/ui';

/**
 * Every operator-set knob, and — the point of the screen — what each one
 * currently resolves to.
 *
 * The row alone would have shown nothing wrong during the longest silent bug this
 * project has had. `sample.py` read this table with `Key={"key": …}` when the
 * partition key is `configKey`, so every pinned lookup raised a
 * ValidationException, the surrounding `except` swallowed it, and the read fell
 * through to a scan. An operator could write the pin row and observe no effect at
 * all. `/catalog/sample` — the free sample every parent is shown before being
 * asked to spend — served a 404 for weeks, and a config screen showing the row
 * would have shown a perfectly healthy pin the entire time.
 *
 * So the value of this page is the dereference: every assetId is looked up
 * server-side and reported found or not, which gives a swallowed exception
 * somewhere to become visible. The partition key's real name is printed for the
 * same reason.
 */
export function Config() {
  const { result, reload } = useAsync(() => getConfig(), []);

  return (
    <>
      <Toolbar>
        <span className="spacer" />
        <button onClick={reload}>Refresh</button>
      </Toolbar>

      <Result of={result} what="config" retry={reload}>
        {(page) => (
          <>
            <p className="muted small">
              Partition key: <code>{page.partitionKey ?? 'configKey'}</code> — not{' '}
              <code>key</code>. Getting that wrong cost weeks, silently, so it is
              written on the screen where the next person will look.
            </p>

            {!page.rows.length ? (
              <Empty what="config rows" />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Value</th>
                    <th>Resolves to</th>
                  </tr>
                </thead>
                <tbody>
                  {page.rows.map((row) => {
                    const broken = row.resolves && row.resolves.found === false;
                    return (
                      <tr key={row.configKey} className={broken ? 'flagged' : ''}>
                        <td>
                          <code>{row.configKey}</code>
                          {row.note && <div className="muted small">{row.note}</div>}
                        </td>
                        <td>
                          <pre className="raw" style={{ maxWidth: 420 }}>
                            {JSON.stringify(row.value ?? {}, null, 2)}
                          </pre>
                        </td>
                        <td>
                          {!row.resolves ? (
                            <span className="muted small">
                              nothing to dereference — this key points at no asset
                            </span>
                          ) : row.resolves.found ? (
                            <>
                              <Pill tone="on">found</Pill>
                              <div className="small">
                                <Link
                                  to={{ name: 'clips', assetId: row.resolves.assetId ?? '' }}
                                >
                                  <code>{row.resolves.canonical || row.resolves.assetId}</code>
                                </Link>
                              </div>
                              <div className="muted small">
                                {row.resolves.kind} · {row.resolves.reuse}
                              </div>
                              <div className="muted small">
                                <code>{row.resolves.storageKey}</code>
                              </div>
                            </>
                          ) : (
                            <>
                              <Pill tone="bad">the asset is gone</Pill>
                              <div className="muted small">
                                Pinned to <Id value={row.resolves.assetId} chars={20} />, which
                                has no row in showling-assets. Anything reading this
                                pin gets nothing — and, if it swallows the miss,
                                gets nothing quietly.
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            <NotBuilt
              what="Editing config from here"
              detail="The API reports writable: false and there is no write route. A knob that changes what every parent is shown, edited from a password-only account with no ledger row anywhere, is not a button this console should grow before MFA does."
            />
          </>
        )}
      </Result>
    </>
  );
}
